import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import SearchResultCard from "./SearchResultCard";
import { Language } from "../../App";
import { resolveAgendaImageUrl } from "../../utils/agendaUtils";
import { _imagesURL } from "../../constants/API";
import defaultAgenda1 from "../../assets/images/default_agenda_1.jpg";
import Avatar from "../../assets/images/icon/placeholder_Awatar.png";
import {
  LuCalendar,
  LuClock,
  LuMapPin,
  LuArrowRight,
  LuCalendarX,
} from "react-icons/lu";

export function EventCard({ item }) {
  const { lang } = useContext(Language);
  const navigate = useNavigate();

  const cleanId = item?.id ? String(item.id).replace(/^(evt-|webinar-|session-)/i, "") : "";
  const coverImage = item?.image || item?.thumbnail || item?.cover || item?.media?.[0];
  const coverUrl = resolveAgendaImageUrl(coverImage, defaultAgenda1);

  const title = item?.title || item?.name || "Medical Event";
  const dateStr = item?.schedule_date || item?.start_date || item?.date || "";
  const timeStr = item?.schedule_time || item?.start_time || item?.time || "";
  const locationStr = item?.location || item?.address || (item?.is_online ? "Online" : "In-Person");

  const hostName =
    item?.user?.name ||
    [item?.user?.firstname, item?.user?.lastname].filter(Boolean).join(" ") ||
    item?.host_name ||
    item?.host ||
    item?.creator_name ||
    "Specialist";

  const hostAvatar = resolveAgendaImageUrl(item?.user?.image, Avatar);

  return (
    <div
      className="search-agenda-card-compact"
      onClick={() => navigate(`/event-details/${cleanId || item?.id}`)}
    >
      <div className="search-agenda-thumb-wrap">
        <img
          src={coverUrl}
          alt={title}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultAgenda1;
          }}
        />
        <span className="search-agenda-type-badge">
          {lang?.event || "Event"}
        </span>
      </div>

      <div className="search-agenda-content">
        <h4 className="search-agenda-title" title={title}>
          {title}
        </h4>

        <div className="search-agenda-meta-row">
          {dateStr && (
            <span className="search-agenda-meta-item">
              <LuCalendar size={13} />
              <span>{dateStr}</span>
            </span>
          )}
          {timeStr && (
            <span className="search-agenda-meta-item">
              <LuClock size={13} />
              <span>{timeStr}</span>
            </span>
          )}
          {locationStr && (
            <span className="search-agenda-meta-item">
              <LuMapPin size={13} />
              <span>{locationStr}</span>
            </span>
          )}
        </div>

        <div className="search-agenda-footer">
          <div className="search-agenda-host-info">
            <img
              src={hostAvatar}
              alt={hostName}
              className="search-agenda-host-avatar"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = Avatar;
              }}
            />
            <span className="search-agenda-host-name">{hostName}</span>
          </div>

          <span className="search-agenda-btn-action">
            <span>{lang?.view_details || "View Details"}</span>
            <LuArrowRight size={13} />
          </span>
        </div>
      </div>
    </div>
  );
}

export default function EventsResultComp({ data, keyword, loading }) {
  const { lang } = useContext(Language);

  if (!loading && (!data || data.length === 0)) {
    return null;
  }

  return (
    <SearchResultCard
      id="events"
      icon={<LuCalendar />}
      title={lang?.events || "Events"}
      count={data?.length}
      link={`/search/events?keyword=${encodeURIComponent(keyword || "")}`}
      seeAll={!loading && data?.length > 2}
    >
      {loading ? (
        <div className="search-agenda-grid">
          {[1, 2].map((n) => (
            <div key={n} className="search-agenda-card-compact">
              <div className="search-agenda-thumb-wrap search-shimmer-box" style={{ width: "100%", height: "140px" }} />
              <div className="search-agenda-content">
                <div className="search-shimmer-box mb-2" style={{ height: "16px", width: "80%" }} />
                <div className="search-shimmer-box mb-3" style={{ height: "13px", width: "60%" }} />
                <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                  <div className="d-flex align-items-center gap-2">
                    <div className="search-shimmer-box rounded-circle" style={{ width: 22, height: 22 }} />
                    <div className="search-shimmer-box" style={{ height: 11, width: 65 }} />
                  </div>
                  <div className="search-shimmer-box" style={{ height: 12, width: 60 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : data && data.length > 0 ? (
        <div className="search-agenda-grid">
          {data.slice(0, 2).map((item, index) => (
            <EventCard key={item?.id || index} item={item} />
          ))}
        </div>
      ) : (
        <div className="search-empty-state-card">
          <div className="search-empty-icon-wrap">
            <LuCalendarX />
          </div>
          <h5 className="search-empty-title">{lang?.no_events_found || "No events found"}</h5>
          <p className="search-empty-desc">
            {lang?.try_different_keyword || "Try a different keyword or check spelling."}
          </p>
        </div>
      )}
    </SearchResultCard>
  );
}
